import { ref } from 'vue';

export type ConfirmType = 'info' | 'warning' | 'danger';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmType;
}

export interface ConfirmState {
  show: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  type: ConfirmType;
  loading: boolean;
  resolve?: (value: boolean) => void;
}

const confirmState = ref<ConfirmState>({
  show: false,
  title: 'Confirm Action',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  type: 'info',
  loading: false,
});

export const useConfirm = () => {
  const confirm = (options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmState.value = {
        show: true,
        title: options.title || 'Confirm Action',
        message: options.message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'info',
        loading: false,
        resolve,
      };
    });
  };

  const handleConfirm = () => {
    if (confirmState.value.resolve) {
      confirmState.value.resolve(true);
    }
    confirmState.value.show = false;
  };

  const handleCancel = () => {
    if (confirmState.value.resolve) {
      confirmState.value.resolve(false);
    }
    confirmState.value.show = false;
  };

  const setLoading = (loading: boolean) => {
    confirmState.value.loading = loading;
  };

  return {
    confirmState,
    confirm,
    handleConfirm,
    handleCancel,
    setLoading,
  };
};

// Export a singleton instance for easy use
export const confirmDialog = {
  show: (options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmState.value = {
        show: true,
        title: options.title || 'Confirm Action',
        message: options.message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'info',
        loading: false,
        resolve,
      };
    });
  },
  
  confirm: (message: string, title?: string): Promise<boolean> => {
    return confirmDialog.show({
      message,
      title,
      type: 'info',
    });
  },

  danger: (message: string, title?: string): Promise<boolean> => {
    return confirmDialog.show({
      message,
      title: title || 'Confirm Deletion',
      confirmText: 'Delete',
      type: 'danger',
    });
  },

  warning: (message: string, title?: string): Promise<boolean> => {
    return confirmDialog.show({
      message,
      title,
      type: 'warning',
    });
  },
};

// Export the state for the component
export { confirmState };

